import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
});

api.interceptors.request.use((config) => {
  try {
    const stored = localStorage.getItem('erbi_current_user');
    if (stored) {
      const user = JSON.parse(stored);
      if (user && user.id) {
        config.headers['X-User-Id'] = user.id;
      }
    }
  } catch {
    // ignore parse errors
  }
  return config;
});

export function fetchUsers() {
  return api.get('/users');
}

export function updateUser(userId, fields) {
  return api.patch(`/users/${userId}`, fields);
}

export function createUser(fields) {
  return api.post('/users', fields);
}

export function fetchVendors() {
  return api.get('/vendors');
}

export function fetchProjects() {
  return api.get('/projects');
}

export function fetchProject(id) {
  return api.get(`/projects/${id}`);
}

export function createProject(data) {
  return api.post('/projects', data);
}

export function updateProject(id, data) {
  return api.patch(`/projects/${id}`, data);
}

export function deleteProject(id) {
  return api.delete(`/projects/${id}`);
}

export function fetchInvoices() {
  return api.get('/invoices/');
}

export function fetchInvoice(id) {
  return api.get(`/invoices/${id}`);
}

export function uploadInvoice(formData) {
  return api.post('/invoices/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export function updateInvoiceStatus(id, newStatus) {
  return api.patch(`/invoices/${id}/status`, { new_status: newStatus });
}

// Approval targets the invoice owner. When no explicit approver is given the
// body omits approver_id so the backend resolves the owner itself.
export function createApprovalRequest(invoiceId, approverId = null) {
  const body = approverId ? { approver_id: approverId } : {};
  return api.post(`/invoices/${invoiceId}/approve-request`, body);
}

export function getPendingApprovals() {
  return api.get('/invoices/approvals/pending');
}

// Processor-only "Batch as Luxembourg" confirm gate. Routes a detected
// Luxembourg invoice from intake (received/under_review) into lux_open +
// the open quarterly batch, instead of sending it for approval.
export function batchLuxembourg(invoiceId) {
  return api.post(`/invoices/${invoiceId}/batch-luxembourg`);
}

export function respondToApproval(requestId, action, notes = null) {
  return api.post(`/invoices/approvals/${requestId}/respond`, { action, notes });
}

export function getPendingCount() {
  return api.get('/invoices/approvals/count');
}

export function getApproverInvoiceIds() {
  return api.get('/invoices/approvals/invoice-ids');
}

// Email "Approve All" magic-link (issue #35). Auth is the token in the URL,
// not X-User-Id — these back the unauthenticated confirmation page.
export function getApproveAllConfirmation(token) {
  return api.get('/invoices/approve-all', { params: { token } });
}

export function consumeApproveAll(token) {
  return api.post('/invoices/approve-all', { token });
}

export function getProjection(viewType) {
  return api.get(`/invoices/projections/${viewType}`);
}

export function createVendor(name) {
  return api.post('/vendors', { name });
}

export function fetchVendor(vendorId) {
  return api.get(`/vendors/${vendorId}`);
}

export function updateVendor(vendorId, data) {
  return api.patch(`/vendors/${vendorId}`, data);
}

export function deleteVendor(vendorId) {
  return api.delete(`/vendors/${vendorId}`);
}

export function fetchVendorAliases(vendorId) {
  return api.get(`/vendors/${vendorId}/aliases`);
}

export function createVendorAlias(vendorId, aliasName) {
  return api.post(`/vendors/${vendorId}/aliases`, { alias_name: aliasName });
}

export function deleteVendorAlias(vendorId, aliasId) {
  return api.delete(`/vendors/${vendorId}/aliases/${aliasId}`);
}

export function updateInvoiceFields(invoiceId, fields) {
  return api.patch(`/invoices/${invoiceId}/fields`, fields);
}

export function getClassificationMappings() {
  return api.get('/invoices/mappings/project-vendor');
}

export function createClassificationMapping(vendorId, projectId, descriptionPattern = null) {
  return api.post('/invoices/mappings/project-vendor', {
    vendor_id: vendorId,
    project_id: projectId,
    description_pattern: descriptionPattern,
  });
}

export function deleteClassificationMapping(mappingId) {
  return api.delete(`/invoices/mappings/project-vendor/${mappingId}`);
}

export function getProjectVendorMappings() {
  return api.get('/invoices/mappings/project-vendor');
}

export function createProjectVendorMapping(projectId, vendorId, glAccount = null, glCostCenter = null) {
  return api.post('/invoices/mappings/project-vendor', {
    project_id: projectId,
    vendor_id: vendorId,
    gl_account: glAccount,
    gl_cost_center: glCostCenter,
  });
}

export function deleteProjectVendorMapping(mappingId) {
  return api.delete(`/invoices/mappings/project-vendor/${mappingId}`);
}

export function fetchInvoiceCandidates(invoiceId) {
  return api.get(`/invoices/${invoiceId}/candidates`);
}

export function fetchSettings() {
  return api.get('/settings');
}

export function updateSettings(data) {
  return api.patch('/settings', data);
}

// Re-evaluate the VAT threshold flag for invoices not yet approved
// (received / under_review / pending_approval). Returns
// { evaluated, changed, newly_vat_eligible, no_longer_vat_eligible }.
export function recomputeVatThreshold() {
  return api.post('/settings/vat-threshold/recompute');
}

export function classifyInvoice(invoiceId, projectId, ownerId = null, updateGlFields = true) {
  return api.post(`/invoices/${invoiceId}/classify`, {
    project_id: projectId,
    owner_id: ownerId,
    update_gl_fields: updateGlFields,
  });
}

export function fetchGlPreview(invoiceId, projectId, vendorId = null) {
  const params = new URLSearchParams({ project_id: projectId });
  if (vendorId) params.append('vendor_id', vendorId);
  return api.get(`/invoices/${invoiceId}/gl-preview?${params.toString()}`);
}

export function fetchAllocations(invoiceId) {
  return api.get(`/invoices/${invoiceId}/allocations`);
}

export function replaceAllocations(invoiceId, allocations) {
  return api.put(`/invoices/${invoiceId}/allocations`, { allocations });
}

// SSE endpoint URL — connects directly to the API server to avoid
// webpack-dev-server's compression middleware which buffers SSE streams.
// In production, a proper reverse proxy (nginx) handles this correctly.
export const SSE_URL =
  process.env.REACT_APP_SSE_URL || `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api/invoices/stream`;

export function deleteInvoice(id) {
  return api.delete(`/invoices/${id}`);
}

export function clearAllInvoices() {
  return api.delete('/invoices/all');
}

export function clearAllBudgetData() {
  return api.delete('/budget/all');
}

export function getProjectVendorMappingsByProject(projectId) {
  return api.get(`/invoices/mappings/project-vendor?project_id=${projectId}`);
}

export function updateProjectVendorMapping(mappingId, data) {
  return api.put(`/invoices/mappings/project-vendor/${mappingId}`, data);
}

export function createMappingInstruction(mappingId, instructionText, weight = 10) {
  return api.post(`/invoices/mappings/project-vendor/${mappingId}/instructions`, {
    instruction_text: instructionText,
    weight,
  });
}

export function updateMappingInstruction(mappingId, instructionId, data) {
  return api.put(`/invoices/mappings/project-vendor/${mappingId}/instructions/${instructionId}`, data);
}

export function deleteMappingInstruction(mappingId, instructionId) {
  return api.delete(`/invoices/mappings/project-vendor/${mappingId}/instructions/${instructionId}`);
}

export function transcribeAudio(audioBlob, filename = 'recording.webm') {
  const formData = new FormData();
  formData.append('file', audioBlob, filename);
  return api.post('/invoices/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export function createMappingKeyword(mappingId, keyword) {
  return api.post(`/invoices/mappings/project-vendor/${mappingId}/keywords`, { keyword });
}

export function deleteMappingKeyword(mappingId, keywordId) {
  return api.delete(`/invoices/mappings/project-vendor/${mappingId}/keywords/${keywordId}`);
}

export function checkKeywordDuplicate(mappingId, keyword) {
  return api.get(`/invoices/mappings/project-vendor/${mappingId}/keywords/check-duplicate?keyword=${encodeURIComponent(keyword)}`);
}

// ---------------------------------------------------------------------------
// VAT workflow
// ---------------------------------------------------------------------------

export function fetchVatDashboardProjection() {
  return api.get('/invoices/projections/vat_dashboard');
}

export function generateVatBatch(
  period,
  vendorId,
  fiscalYear = null,
  requestorName = null,
  approverName = null,
  fxRateOverride = null,
  fxRateOverrideDate = null,
  payingBankAccountId = null,
  companyCodeId = null,
  batchId = null
) {
  const body = { period, vendor_id: vendorId };
  if (fiscalYear != null) body.fiscal_year = fiscalYear;
  // Optional cover-sheet "Name & Title" overrides (P0 item 3). Blank/omitted
  // lets the backend apply its defaults.
  if (requestorName != null) body.requestor_name = requestorName;
  if (approverName != null) body.approver_name = approverName;
  // Optional FX rate override. Send the chosen from-currency→USD multiplier
  // only when the user changed from the dynamic default (picked another date
  // or typed an override). A picked date sends fx_rate_override_date; a
  // free-typed override sends a null date.
  if (fxRateOverride != null) body.fx_rate_override = fxRateOverride;
  if (fxRateOverrideDate != null) body.fx_rate_override_date = fxRateOverrideDate;
  // Optional "Transfer funds from" paying bank. When set, the backend
  // snapshots the bank's fields onto the batch; null/omitted leaves the
  // EFT section blank.
  if (payingBankAccountId != null) body.paying_bank_account_id = payingBankAccountId;
  // Optional JE "Co Code" destination entity. When set, the backend
  // snapshots the code's fields onto the batch; null/omitted falls back to
  // the historical hardcoded 1256 in the rendered JE.
  if (companyCodeId != null) body.company_code_id = companyCodeId;
  // Target draft batch. Sent when per-invoice packages are on, where a
  // vendor + period holds one draft batch per invoice and only this one may
  // be finalized; omitted keeps the historical vendor + period scope.
  if (batchId != null) body.batch_id = batchId;
  return api.post('/vat/batches', body);
}

// FX rate options for a VAT batch's invoice date (P0 FX picker). Returns
// { currency, default, current_override, available }.
export function getVatFxOptions(batchId) {
  return api.get(`/vat/batches/${batchId}/fx-options`);
}

// Pre-batch FX rate options for the "Generate VAT Package" button, where
// clicking generate is what finalizes the batch. Computes the prospective
// currency + default rate from the eligible vat_open invoices for
// (period, vendor_id, fiscal_year). `current_override` is always null.
// Returns the same FxOptionsResponse shape as getVatFxOptions.
//
// `batchId` narrows that set to one draft batch's own invoices — sent from
// the per-invoice rows so the picker prices the single invoice being
// packaged rather than the whole vendor group (which would resolve the rate
// as of the wrong invoice date, and no currency at all for a mixed group).
export function getVatFxOptionsPreBatch(period, vendorId, fiscalYear, batchId = null) {
  const params = new URLSearchParams({
    period,
    vendor_id: vendorId,
    fiscal_year: fiscalYear,
  });
  if (batchId != null) params.set('batch_id', batchId);
  return api.get(`/vat/fx-options?${params.toString()}`);
}

export function getVatBatch(batchId) {
  return api.get(`/vat/batches/${batchId}`);
}

export function downloadVatBatch(batchId) {
  return api.get(`/vat/batches/${batchId}/download`);
}

// Regenerate accepts the same optional FX-picker override as generate: the
// chosen from-currency→USD multiplier and the picked rate_date (null for a
// free-typed override). Both null leaves the batch on its stored/dynamic rate.
export function regenerateVatBatch(
  batchId,
  requestorName = null,
  approverName = null,
  fxRateOverride = null,
  fxRateOverrideDate = null,
  payingBankAccountId = undefined,
  companyCodeId = undefined
) {
  const body = {};
  // Names are sent whenever provided — including empty strings, which the
  // backend treats as "reset to the standard default". null/undefined means
  // "leave the stored value unchanged".
  if (requestorName != null) body.requestor_name = requestorName;
  if (approverName != null) body.approver_name = approverName;
  if (fxRateOverride != null) body.fx_rate_override = fxRateOverride;
  if (fxRateOverrideDate != null) body.fx_rate_override_date = fxRateOverrideDate;
  // "Transfer funds from" paying bank is PRESENCE-SENSITIVE on the backend,
  // so (unlike the fields above) an explicit null must reach the wire:
  // undefined = omit the key (keep the batch's stored snapshot), null =
  // send null (clear id + snapshot), a UUID = re-snapshot from that record.
  if (payingBankAccountId !== undefined) body.paying_bank_account_id = payingBankAccountId;
  // JE "Co Code" destination is PRESENCE-SENSITIVE too, same sentinel
  // semantics: undefined = omit the key (keep the batch's stored snapshot),
  // null = send null (clear — the JE falls back to the hardcoded 1256), a
  // UUID = re-snapshot from that record.
  if (companyCodeId !== undefined) body.company_code_id = companyCodeId;
  return api.post(`/vat/batches/${batchId}/regenerate`, body);
}

export function submitVatBatch(batchId) {
  return api.post(`/vat/batches/${batchId}/submit`);
}

export function getVatBatchChecklist(batchId) {
  return api.get(`/vat/batches/${batchId}/checklist`);
}

export function toggleVatBatchChecklistItem(batchId, itemId, completed) {
  return api.put(`/vat/batches/${batchId}/checklist/${itemId}`, { completed });
}

// Approve every pending-approval invoice in a VAT package as a single unit.
// Returns { batch_id, approved_count }. 400 if checklist incomplete.
export function approveVatPackage(batchId) {
  return api.post(`/vat/batches/${batchId}/approve`);
}

export function fetchInvoiceVatSection(invoiceId) {
  return api.get(`/invoices/${invoiceId}/vat-section`);
}

// ---------------------------------------------------------------------------
// Paying banks ("Transfer funds from" on the EFT Request Form)
// ---------------------------------------------------------------------------
// GET is open to any authenticated user (powers the package modal dropdown);
// create/update/delete are processor/admin-only (server-enforced).

export function fetchPayingBanks(activeOnly = false) {
  return api.get('/vat/paying-banks', {
    params: activeOnly ? { active_only: true } : {},
  });
}

export function createPayingBank(body) {
  return api.post('/vat/paying-banks', body);
}

export function updatePayingBank(id, body) {
  return api.put(`/vat/paying-banks/${id}`, body);
}

export function deletePayingBank(id) {
  return api.delete(`/vat/paying-banks/${id}`);
}

// ---------------------------------------------------------------------------
// Company codes (JE "Co Code" destinations on the Tax Invoice Template)
// ---------------------------------------------------------------------------
// GET is open to any authenticated user (powers the package modal dropdown);
// create/update/delete are processor/admin-only (server-enforced).

export function fetchCompanyCodes(activeOnly = false) {
  return api.get('/vat/company-codes', {
    params: activeOnly ? { active_only: true } : {},
  });
}

export function createCompanyCode(body) {
  return api.post('/vat/company-codes', body);
}

export function updateCompanyCode(id, body) {
  return api.put(`/vat/company-codes/${id}`, body);
}

export function deleteCompanyCode(id) {
  return api.delete(`/vat/company-codes/${id}`);
}

export function createVendorBankAccount(body) {
  return api.post('/vendor-bank-accounts', body);
}

export function updateVendorBankAccount(id, body) {
  return api.patch(`/vendor-bank-accounts/${id}`, body);
}

// ---------------------------------------------------------------------------
// Luxembourg workflow
// ---------------------------------------------------------------------------

export function fetchLuxDashboardProjection() {
  return api.get('/invoices/projections/lux_dashboard');
}

export function generateLuxBatch(
  fiscalYear,
  quarter,
  fxRateOverride = null,
  fxRateOverrideDate = null
) {
  const body = { fiscal_year: fiscalYear, quarter };
  // Optional FX rate override (from-currency→USD multiplier). Send only when
  // the user changed from the dynamic default; a picked date sends
  // fx_rate_override_date, a free-typed override sends a null date.
  if (fxRateOverride != null) body.fx_rate_override = fxRateOverride;
  if (fxRateOverrideDate != null) body.fx_rate_override_date = fxRateOverrideDate;
  return api.post('/lux/batches', body);
}

// FX rate options for a Lux batch's invoice date (P0 FX picker). Returns
// { currency, default, current_override, available }.
export function getLuxFxOptions(batchId) {
  return api.get(`/lux/batches/${batchId}/fx-options`);
}

// Pre-batch FX rate options for the "Generate Category B + AP Request Package"
// button, where clicking generate is what creates the quarterly batch (so there
// is no batch id yet). Computes the prospective currency + default rate from the
// eligible lux_open invoices for (fiscal_year, quarter). `current_override` is
// always null. Returns the same FxOptionsResponse shape as getLuxFxOptions.
export function getLuxFxOptionsPreBatch(fiscalYear, quarter) {
  const params = new URLSearchParams({ fiscal_year: fiscalYear, quarter });
  return api.get(`/lux/fx-options?${params.toString()}`);
}

export function getLuxBatch(batchId) {
  return api.get(`/lux/batches/${batchId}`);
}

export function downloadLuxBatch(batchId) {
  return api.get(`/lux/batches/${batchId}/download`);
}

// Force-rebuild ONLY the Category B package (local currency — no FX/modal) and
// return a presigned URL { url, expires_in_seconds }. Does NOT touch the AP
// request zip.
export function regenerateLuxCategoryB(batchId) {
  return api.post(`/lux/batches/${batchId}/regenerate-category-b`);
}

// Download the EXISTING AP request zip without rebuilding it. Returns
// { url, expires_in_seconds }; 400 if the zip has not been generated yet.
export function downloadLuxApRequestFile(batchId) {
  return api.get(`/lux/batches/${batchId}/ap-request-file`);
}

// Per-vendor AP request package (zip) generated alongside the Category B
// Approval Request (P0 item 4b). Posts the optional cover-sheet "Name & Title"
// overrides ({ requestor_name, approver_name } — either may be null/blank to
// let the backend apply the same VAT defaults), plus the optional FX-picker
// override ({ fx_rate_override, fx_rate_override_date } — the from-currency→USD
// multiplier and the picked rate_date, both null to use the dynamic default).
// Returns { url, expires_in_seconds }.
export function downloadLuxApRequest(batchId, body = {}) {
  return api.post(`/lux/batches/${batchId}/download-ap-request`, body);
}

// Upload one or many hand-signed documents to a Lux batch (P0 item 4d). The
// endpoint accepts a repeated `files` field.
export function uploadLuxSignedDocuments(batchId, files) {
  const form = new FormData();
  for (const file of files) {
    form.append('files', file);
  }
  return api.post(`/lux/batches/${batchId}/signed-approval`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// List the signed documents uploaded against a Lux batch. Each row carries
// invoice_id + document_id + filename for building the preview URL.
export function getLuxSignedDocuments(batchId) {
  return api.get(`/lux/batches/${batchId}/signed-documents`);
}

export function submitLuxBatch(batchId) {
  return api.post(`/lux/batches/${batchId}/submit`);
}

export function getLuxBatchChecklist(batchId) {
  return api.get(`/lux/batches/${batchId}/checklist`);
}

export function toggleLuxBatchChecklistItem(batchId, itemId, completed) {
  return api.put(`/lux/batches/${batchId}/checklist/${itemId}`, { completed });
}

// Approve every pending-approval invoice in a Luxembourg package as a single
// unit. Returns { batch_id, approved_count }. 400 if checklist incomplete.
export function approveLuxPackage(batchId) {
  return api.post(`/lux/batches/${batchId}/approve`);
}

export default api;
